import { Colors, Icon, Intent } from "@blueprintjs/core";
import type { Hit } from "instantsearch.js";

import { APP_PROFILE } from "@/components/helpers/configuration/constants";
import { Box, Flex } from "@/components/layout/flexbox";
import { EmailStatus } from "@/components/pieces/form/EmailMultiselect";
import { Avatar } from "@/components/pieces/users/Avatar";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { MiniUserType } from "@/graphql";

const emailStatusStyle: Record<
  EmailStatus,
  { color?: string; tooltipText?: string }
> = {
  [EmailStatus.canBeInvited]: {
    color: Colors.GREEN3,
    tooltipText:
      "This person will be invited to join the workspace. This will use an available seat.",
  },
  [EmailStatus.existedButNotInProgram]: {
    color: Colors.GREEN3,
    tooltipText:
      "This existing workspace user will be invited to join the program. This won't use a seat.",
  },
  [EmailStatus.existed]: {
    tooltipText:
      "This person is already a workspace member. No new invitation will be sent.",
  },
  [EmailStatus.pendingInv]: {
    tooltipText:
      "This person already has a pending workspace invitation. No new invitation will be sent.",
  },
  [EmailStatus.invalid]: {
    color: Colors.RED3,
    tooltipText:
      "This email address is invalid. Please correct it to send an invitation.",
  },
  [EmailStatus.existedProgramUser]: {
    tooltipText:
      "This person is already in the program. No new invitation will be sent.",
  },
} as const;

interface EmailSuggestionItemProps {
  suggestion: Hit;
  isTag?: boolean;
  status?: EmailStatus;
}

export function EmailSuggestionItem({
  suggestion,
  isTag = false,
  status,
}: EmailSuggestionItemProps) {
  const showWarningIcon = isTag && status === EmailStatus.invalid;
  const email = suggestion?.email || "";
  const displayText = suggestion?.name
    ? suggestion?.email && !isTag
      ? `${suggestion.name} (${suggestion.email})`
      : suggestion.name
    : email;

  const style = (isTag && status && emailStatusStyle[status]) || {};

  const user: MiniUserType = {
    name: suggestion.name,
    id: suggestion.user_id,
    photo: suggestion.photo && `${APP_PROFILE.apiUrl}/images/avatar/${email}`,
    email: email,
  };

  const content = (
    <Flex
      alignItems="center"
      css={{
        width: "100%",
        position: "relative",
        paddingRight: 0,
        maxWidth: isTag ? "180px" : "100%",
      }}
    >
      <Box mr="4px" flexShrink={0}>
        {showWarningIcon ? (
          <Icon icon="warning-sign" intent={Intent.DANGER} size={16} />
        ) : (
          <Avatar size={16} user={user} />
        )}
      </Box>
      <Box
        color={style.color}
        css={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          lineHeight: "24px",
          width: "100%",
        }}
      >
        {displayText}
      </Box>
    </Flex>
  );

  return isTag ? (
    <SimpleTooltip content={<Box>{style.tooltipText || email}</Box>}>
      {content}
    </SimpleTooltip>
  ) : (
    <Box
      style={{
        width: "100%",
      }}
    >
      {content}
    </Box>
  );
}
